(ns practitest.frontend.common.keyboard-shortcuts
  (:require ["react-hotkeys-hook" :refer [HotkeysProvider useHotkeys useHotkeysContext]]
            ["react" :refer [useEffect]]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [re-frame.core :as re-frame]
            [practitest.frontend.common.dom :as dom]))

;; As we have some pages defined in Ruby on the server side,
;; we need this to conditionally disabling global mousetrap shortcuts what currently handled in React for current page
(when-not (.-activeShortcuts js/window)
  (set! (.-activeShortcuts js/window) (js/Set.)))

(defn- add-shortcut-fc
  "Functional react component for use hook useHotkeys from react-hotkeys-hook. Works with :f> modifier"
  [props]
  (let [{:keys [handler shortcut-name] {:keys [combo scope allow-on-form-edit]} :shortcut} props
        keyboard-shortcuts-enabled (:keyboard_shortcuts_enabled @(re-frame/subscribe [::subs/current-user]))
        enable-on-form-tags (when allow-on-form-edit ["INPUT", "TEXTAREA"])]

    (useEffect (fn [] (.add (.-activeShortcuts js/window) shortcut-name)
                 (fn [] (.delete (.-activeShortcuts js/window) shortcut-name))) #js [])

    (useHotkeys combo (fn [] (handler))
                #js {:scopes (clj->js scope)
                     :preventDefault true
                     :enabled keyboard-shortcuts-enabled
                     :enableOnFormTags enable-on-form-tags
                     :deps (clj->js [keyboard-shortcuts-enabled scope enable-on-form-tags])
                     }))
  [:<>])

(defn- enable-scope-fc
  "Functional react component for use hook useHotkeysContext from react-hotkeys-hook. Works with :f> modifier"
  [props]
  (let [{:keys [enable-scopes disable-scopes]} props
        context (useHotkeysContext)
        active-scopes  (-> context
                           .-activeScopes
                           js->clj
                           set)
        enableScope   (.-enableScope  context)
        disableScope  (.-disableScope context)]
    (useEffect (fn []
                 (doseq [scope-to-enable enable-scopes]
                   (when-not (active-scopes scope-to-enable) (enableScope scope-to-enable)))
                 (doseq [scope-to-disable disable-scopes]
                   (when (active-scopes scope-to-disable) (disableScope scope-to-disable)))
                 ;will unmount component
                 (fn []
                   (doseq [scope-to-enable (vec active-scopes)]
                     (enableScope scope-to-enable))
                   (doseq [scope-to-disable enable-scopes]
                     (disableScope scope-to-disable))))
               #js [])
    [:<>]))

(def ^:private shortcuts
  "Shortcut's configs. Includes info about scopes, keyword combination, description"
  {:entity-form {:save {:combo "mod+alt+s" :scope "entity-form" :disable-scopes [] :description "Save entity"
                        :allow-on-form-edit true}
                 :next-tab {:combo "backquote" :scope "entity-form" :disable-scopes [] :description "Next tab"}
                 :prev-tab {:combo "shift+backquote" :scope "entity-form" :disable-scopes [] :description "Previous tab"}
                 :edit-name {:combo "mod+shift+e" :scope "entity-form" :disable-scopes [] :description "Focus on name entity"
                             :allow-on-form-edit true}
                 :add-attachment {:combo "mod+shift+a" :scope "entity-form" :disable-scopes [] :description "Add attachment"
                                  :allow-on-form-edit true}
                 :add-comments {:combo "mod+shift+c" :scope "entity-form" :disable-scopes [] :description "Add comments"
                                :allow-on-form-edit true}
                 :add-test-step {:combo "mod+shift+s" :scope "entity-form" :disable-scopes [] :description "Add test step"
                                 :allow-on-form-edit true}}

   :modal {:save {:combo "mod+alt+s" :scope "modal" :disable-scopes ["entity-form"] :description "Save/create entity in modal"
                  :allow-on-form-edit true}}
   :edit-et-test {:add-et-annotation {:combo "mod+alt+a" :scope "edit-et-test" :disable-scopes []
                                      :description "Add annotation to ET test from set's run screen" :allow-on-form-edit true}
                  :run-next-test {:combo "shift+period" :scope "edit-et-test" :disable-scopes []
                                      :description "Add annotation to ET test from set's run screen"}}
   :global {:focus-on-search {:combo "slash" :scope "global.focus" :disable-scopes [] :description "Focus om main Search"}
            :go-to-requirements {:combo "g+r" :scope "global.navigation" :disable-scopes [] :description "Go to requirements"}
            :go-to-dashboard {:combo "g+d" :scope "global.navigation" :disable-scopes [] :description "Go to dashboard"}
            :go-to-milestones {:combo "g+m" :scope "global.navigation" :disable-scopes [] :description "Go to milestones"}
            :go-to-issues {:combo "g+i" :scope "global.navigation" :disable-scopes [] :description "Go to issues"}
            :go-to-tests {:combo "g+t" :scope "global.navigation" :disable-scopes [] :description "Go to tests"}
            :go-to-testsets {:combo "g+s" :scope "global.navigation" :disable-scopes [] :description "Go to test Sets"}
            :go-to-reports {:combo "g+p" :scope "global.navigation" :disable-scopes [] :description "Go to reports"}
            :go-to-new-requirements {:combo "n+r" :scope "global.navigation" :disable-scopes [] :description "Go to new requirements"}
            :go-to-new-milestones {:combo "n+m" :scope "global.navigation" :disable-scopes [] :description "Go to new milestones"}
            :go-to-new-issue {:combo "n+i" :scope "global.navigation" :disable-scopes [] :description "Go to new issue"}
            :go-to-new-test {:combo "n+t" :scope "global.navigation" :disable-scopes [] :description "Go to new test"}
            :go-to-new-testset {:combo "n+s" :scope "global.navigation" :disable-scopes [] :description "Go to new testset"}
            :go-to-new-report {:combo "n+p" :scope "global.navigation" :disable-scopes [] :description "Go to new report"}}})

(defn keyboard-shortcuts-provider
  "This component wrapper allows shortcuts to use scopes and init 'global' scope"
  [root]
  (into [:> HotkeysProvider {:initiallyActiveScopes #js ["global"]}] [root]))

(defn add-shortcut
  "Add shortcut component. Accept keys :shortcut and :handler. :shortcut key is a path for shortcuts config as vector. 
   For example: [:global :focus-on-search]"
  [{:keys [shortcut handler]}]
  [:f> add-shortcut-fc {:shortcut (get-in shortcuts shortcut)
                        :handler handler
                        :shortcut-name (name (second shortcut))}])

(defn enable-scope
  "This component enable/disable shortcut scopes on component mount/unmount. 
   Accept :shortcuts-type from shortcut configs. For example {:shortcuts-type :entity-form}"
  [{:keys [shortcuts-type]}]
  (let [shortcuts (shortcuts-type shortcuts)
        enable-scopes (reduce (fn [acc [_ {:keys [scope]}]]
                                (conj acc scope))
                              #{}
                              shortcuts)
        disable-scopes (->> shortcuts
                            vals
                            (mapcat :disable-scopes)
                            set)]
    [:f> enable-scope-fc {:enable-scopes enable-scopes
                          :disable-scopes disable-scopes}]))

(defn- goto-handler
  "Generic handler for all go-to shortcuts"
  [route params]
  (fn []
    (dom/navigate! (broutes/get-url-view route nil (when-not (empty? params) params)))))

(def ^:private goto-shortcuts
  "Go-to shortcuts config with paths"
  {:go-to-requirements  "requirements#create"
   :go-to-dashboard   "summary#index"
   :go-to-milestones "milestones#create"
   :go-to-issues "issues#index"
   :go-to-tests "qtest_designs#index"
   :go-to-testsets "suites#index"
   :go-to-reports "reports#index"
   :go-to-new-requirements "requirements#new"
   :go-to-new-milestones "milestones#new"
   :go-to-new-issue "issues#new"
   :go-to-new-test "qtest_designs#new"
   :go-to-new-testset "common#create_testset"
   :go-to-new-report "reports#index"
   })

(def ^:private goto-with-focus
  "Go-to shortcuts that after redirect focus on entity name input"
  #{:go-to-new-requirements :go-to-new-milestones :go-to-new-issue :go-to-new-test :go-to-new-testset})

(def ^:private goto-with-open-modal
  "Go-to shortcuts that after redirect open modal"
  #{:go-to-new-report})

(defn- get-goto-params [shortcut]
  (cond-> {}
    (goto-with-focus shortcut) (assoc :should-focus true)
    (goto-with-open-modal shortcut) (assoc :open-modal true)))

(defn add-goto-shortcuts
  "Component that add all go-to shortcuts"
  []
  (into
   [:<>]
   (for [[shortcut route] goto-shortcuts]
     ^{:key shortcut}
     [add-shortcut {:shortcut [:global shortcut] :handler  (goto-handler route (get-goto-params shortcut))}])))
